import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { 
    getDailyDistributions, 
    getSalaryStatementSummary,
    clearSalarySystemData
} from '../../actions/salarySystem';
import useActiveFirm from '../../hooks/useActiveFirm';
import DailyDistributionsTabExact from './DailyDistributionsTabExact';
import SalaryStatementsTab from './SalaryStatementsTab';
import './SalarySystem.css';

const SalarySystem = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { 
        dailyDistributions, 
        salaryStatementSummary
    } = useSelector(state => state.salarySystem);

    // Use global active firm from sidebar switcher
    const { activeFirm, firmId: globalFirmId, firmName, loading: firmLoading } = useActiveFirm();
    
    // Use global firm ID as selected firm
    const selectedFirm = globalFirmId;

    // Get active tab from URL path
    const getActiveTabFromPath = () => {
        const path = location.pathname;
        if (path.includes('/daily-distributions')) return 'daily-distributions';
        if (path.includes('/salary-statements')) return 'salary-statements';
        if (path === '/salary-system') return 'daily-distributions'; // Base route defaults to daily-distributions
        return 'daily-distributions'; // default
    };

    const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
    const [localLoading, setLocalLoading] = useState(false);
    const loadedFirmRef = useRef(null);

    // Update active tab when URL changes
    useEffect(() => {
        setActiveTab(getActiveTabFromPath());
    }, [location.pathname]);

    // Auto-redirect to daily-distributions if on base /salary-system route
    useEffect(() => {
        const currentPath = location.pathname;
        if (currentPath === '/salary-system' && selectedFirm) {
            const today = new Date().toISOString().split('T')[0];
            const newUrl = `/salary-system/daily-distributions?firm_id=${selectedFirm}&from_date=${today}&to_date=${today}`;
            history.replace(newUrl);
        }
    }, [selectedFirm, history, location.pathname]);

    const loadInitialData = useCallback(async () => {
        if (loadedFirmRef.current === selectedFirm) return; // Prevent multiple calls for same firm
        
        try {
            setLocalLoading(true);
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            
            const dateParams = {
                from_date: todayString,
                to_date: todayString
            };
            
            await Promise.all([
                dispatch(getDailyDistributions({ 
                    firm_id: selectedFirm,
                    ...dateParams
                })),
                dispatch(getSalaryStatementSummary({ 
                    firm_id: selectedFirm, 
                    ...dateParams
                }))
            ]);
            
            loadedFirmRef.current = selectedFirm; // Mark this firm as loaded
        } catch (error) {
            console.error('Failed to load initial data:', error);
        } finally {
            setLocalLoading(false);
        }
    }, [selectedFirm, dispatch]);

    useEffect(() => {
        let isMounted = true;
        
        // Load initial data when component mounts
        if (selectedFirm && isMounted) {
            loadInitialData();
        }
        
        return () => {
            isMounted = false;
        };
    }, [selectedFirm, loadInitialData]);

    // Clear data when firm changes (from sidebar)
    useEffect(() => {
        if (selectedFirm && loadedFirmRef.current !== selectedFirm) {
            dispatch(clearSalarySystemData());
            loadedFirmRef.current = null;
        }
    }, [selectedFirm, dispatch]);

    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
        
        // Get current date for parameters
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        
        // Navigate to the corresponding route with firm_id and date parameters
        let url;
        if (selectedFirm) {
            url = `/salary-system/${tabName}?firm_id=${selectedFirm}&from_date=${todayString}&to_date=${todayString}`;
        } else {
            url = `/salary-system/${tabName}`;
        }
        history.push(url);
    };

    const renderActiveTab = () => {
        if (!selectedFirm) {
            return (
                <div className="firm-selection">
                    <div style={{ textAlign: 'center', color: '#6c757d', padding: '40px' }}>
                        <p>Iltimos, chap paneldan firmani tanlang.</p>
                    </div>
                </div>
            );
        }

        switch (activeTab) {
            case 'daily-distributions':
                return <DailyDistributionsTabExact firmId={selectedFirm} />;
            case 'salary-statements':
                return <SalaryStatementsTab firmId={selectedFirm} />;
            default:
                return <DailyDistributionsTabExact firmId={selectedFirm} />;
        }
    };

    return (
        <div className="salary-system">
            <div className="content-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2>
                        Employee Salary System
                        {firmName && (
                            <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#6c757d', marginLeft: '12px' }}>
                                — {firmName}
                            </span>
                        )}
                    </h2>
                </div>

                <div className="tab-navigation">
                    <button 
                        className={`tab-button ${activeTab === 'daily-distributions' ? 'active' : ''}`}
                        onClick={() => handleTabChange('daily-distributions')}
                    >
                        Daily Distributions
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'salary-statements' ? 'active' : ''}`}
                        onClick={() => handleTabChange('salary-statements')}
                    >
                        Salary Statements
                    </button>
                </div>

                <div className="tab-content">
                    {localLoading ? (
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>Loading...</p>
                        </div>
                    ) : (
                        renderActiveTab()
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalarySystem;
